import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { MatTableModule } from '@angular/material/table';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { environment } from 'enviroments/environment';
import { Tipologia } from 'app/modules/models/tipologia.model';
import { RegistrosService } from 'app/modules/admin/registros/registros.service';
import { AgregarCatalogoComponent } from 'app/modules/admin/catalogos/agregar-catalogo/agregar-catalogo.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CatalogosService } from './catalogos.service';
import { ApiService } from 'app/core/services/api.service';
import { ControlAccesoDirective } from 'app/shared/directives/control-acceso.directive';

interface CatalogoNode {
    name: string;
    tipologiaId?: number;
    valor1?: string;
    valor2?: string;
    children?: CatalogoNode[];
    expandable?: boolean;
    kind?: 'areas-root' | 'orientaciones-root' | 'carreras-root' | 'area' | 'subarea' | 'orientacion' | 'carrera';
    loading?: boolean;
}

@Component({
    selector: 'app-catalogos',
    templateUrl: 'catalogos.component.html',
    standalone: true,
    imports:[
        CommonModule,
        MatRadioModule,
        MatSidenavModule,
        MatTreeModule,
        MatIconModule,
        MatButtonModule,
        MatDialogModule,
        MatMenuModule,
        MatTableModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ControlAccesoDirective
    ]
})

export class CatalogosComponent implements OnInit, OnDestroy {
    drawerMode: 'side' | 'over' = 'side';
    drawerOpened = true;
    isScreenSmall = false;
    selectedCiclo: 'basico' | 'diversificado' = 'basico';
    isLoading = false;
    loadError = '';

    readonly treeControl = new NestedTreeControl<CatalogoNode>(node => node.children ?? []);
    readonly dataSource = new MatTreeNestedDataSource<CatalogoNode>();
    private readonly _destroy$ = new Subject<void>();
    private readonly _tipologiasCache = new Map<number, Tipologia[]>();
    @ViewChild('matDrawer', { static: true }) matDrawer!: MatDrawer;

    displayedColumns: string[] = ['id', 'descripcion', 'prefijo', 'correlativo', 'acciones'];
    tableRows: Tipologia[] = [];
    tableTitle = 'Seleccione un elemento';
    isTableLoading = false;
    selectedNodeId: number | null = null;
    selectedRootKind: CatalogoNode['kind'] | null = null;
    selectedNodeName = '';
    searchTerm = '';

    constructor(
        private _dialog: MatDialog,
        private _registrosService: RegistrosService,
        private _catalogosService: CatalogosService,
        private _apiService: ApiService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _changeDetectorRef: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.cargarArbol();
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._destroy$))
            .subscribe(({ matchingAliases }) => {
                const esEscritorio =
                    matchingAliases.includes('md') ||
                    matchingAliases.includes('lg') ||
                    matchingAliases.includes('xl');
                if (esEscritorio) {
                    this.drawerMode = 'side';
                    this.drawerOpened = true;
                } else {
                    this.drawerMode = 'over';
                    this.drawerOpened = false;
                }
                this._changeDetectorRef.markForCheck();
            });
        this.treeControl.expansionModel.changed
            .pipe(takeUntil(this._destroy$))
            .subscribe((change) => {
                if (change.added?.length) {
                    change.added.forEach(node => this.cargarHijos(node));
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    tieneHijos = (_: number, node: CatalogoNode) => !!node.expandable;

    onCambioCiclo(event: MatRadioChange): void {
        const value = event.value === 'diversificado' ? 'diversificado' : 'basico';
        if (this.selectedCiclo === value) {
            return;
        }
        this.selectedCiclo = value;
        this._tipologiasCache.clear();
        this.resetearTabla();
        this.cargarArbol();
    }

    onSeleccionarNodo(node: CatalogoNode): void {
        if (!node?.tipologiaId) {
            if (node?.kind === 'areas-root' || node?.kind === 'orientaciones-root' || node?.kind === 'carreras-root') {
                this.selectedNodeId = null;
                this.selectedRootKind = node.kind;
                this.selectedNodeName = node.name;
                this.mostrarTablaDesdeNodos(
                    node,
                    node.kind === 'areas-root'
                        ? 'Areas'
                        : node.kind === 'orientaciones-root'
                            ? 'Orientaciones'
                            : 'Carreras'
                );
            }
            return;
        }

        this.selectedNodeId = node.tipologiaId;
        this.selectedRootKind = null;
        this.selectedNodeName = node.name;

        if (node.kind === 'area') {
            this.cargarSubareasPorArea(node);
            return;
        }

        if (this.selectedCiclo === 'diversificado' && node.kind === 'subarea') {
            this.cargarOrientacionesOcupacionales(node);
            return;
        }

        if (this.selectedCiclo === 'basico' && node.kind === 'orientacion') {
            this.cargarOrientacionesOcupacionales(node);
            return;
        }
    }

    estaSeleccionado(node: CatalogoNode): boolean {
        return !!node?.tipologiaId && this.selectedNodeId === node.tipologiaId;
    }

    esNodoRaiz(node: CatalogoNode): boolean {
        return node?.kind === 'areas-root' || node?.kind === 'orientaciones-root' || node?.kind === 'carreras-root';
    }

    alternarDrawer(): void {
        if (this.isScreenSmall) {
            this.matDrawer?.toggle();
        }
    }

    alternarYSeleccionarNodo(node: CatalogoNode): void {
        if (node?.expandable) {
            this.treeControl.toggle(node);
        }
        this.onSeleccionarNodo(node);
    }

    abrirModalRegistro(): void {
        const tipologiaPadreId = this.obtenerTipologiaPadreId();
        const dialogRef = this._dialog.open(AgregarCatalogoComponent, {
            data: {
                tipologiaPadreId,
                titulo: this.selectedNodeName
            }
        });

        dialogRef.afterClosed().subscribe((guardado) => {
            if (guardado) {
                this._tipologiasCache.clear();
                this.cargarArbol();
            }
        });
    }

    abrirModalEdicion(tipologia: Tipologia): void {
        const dialogRef = this._dialog.open(AgregarCatalogoComponent, {
            data: {
                tipologia
            }
        });

        dialogRef.afterClosed().subscribe((guardado) => {
            if (guardado) {
                this._tipologiasCache.clear();
                this.cargarArbol();
            }
        });
    }

    puedeAgregar(): boolean {
        return !!this.obtenerTipologiaPadreId();
    }

    private async cargarArbol(): Promise<void> {
        this.isLoading = true;
        this.loadError = '';
        this.dataSource.data = [];
        this.treeControl.dataNodes = [];

        try {
            const nodes =
                this.selectedCiclo === 'diversificado'
                    ? await this.construirArbolDiversificado()
                    : await this.construirArbolBasico();
            this.dataSource.data = nodes;
            this.treeControl.dataNodes = nodes;
            if (this.selectedRootKind) {
                const rootNode = nodes.find((node) => node.kind === this.selectedRootKind);
                if (rootNode) {
                    this.onSeleccionarNodo(rootNode);
                }
            } else if (this.selectedNodeId) {
                const buscarNodo = (lista: CatalogoNode[]): CatalogoNode | null => {
                    for (const nodo of lista) {
                        if (nodo.tipologiaId === this.selectedNodeId) {
                            return nodo;
                        }
                        if (nodo.children?.length) {
                            const encontrado = buscarNodo(nodo.children);
                            if (encontrado) {
                                return encontrado;
                            }
                        }
                    }
                    return null;
                };
                const nodoSeleccionado = buscarNodo(nodes);
                if (nodoSeleccionado) {
                    this.onSeleccionarNodo(nodoSeleccionado);
                }
            } else {
                this.seleccionarNodoInicial(nodes);
            }
        } catch (error) {
            this.loadError = 'No se pudieron cargar los catalogos.';
            this.dataSource.data = [];
        } finally {
            this.isLoading = false;
        }
    }

    private async construirArbolBasico(): Promise<CatalogoNode[]> {
        const [areas, orientaciones] = await Promise.all([
            this.obtenerTipologias(environment.TIPOLOGIA_ID_AREA_CARRERA_BASICO),
            this.obtenerTipologias(environment.TIPOLOGIA_ID_ORIENTACION_BASICO)
        ]);

        return [
            {
                name: 'Areas',
                kind: 'areas-root',
                expandable: true,
                children: areas.map(area => this.crearNodo(area, undefined, 'area', true))
            },
            {
                name: 'Orientaciones',
                kind: 'orientaciones-root',
                expandable: true,
                children: orientaciones.map(orientacion =>
                    this.crearNodo(orientacion, undefined, 'orientacion', true)
                )
            }
        ];
    }

    private async construirArbolDiversificado(): Promise<CatalogoNode[]> {
        const [areas, carreras] = await Promise.all([
            this.obtenerTipologias(environment.TIPOLOGIA_ID_AREA_CARRERA_DIVERSIFICADO),
            this.obtenerTipologias(environment.TIPOLOGIA_ID_CARRERA_DIVERSIFICADO)
        ]);

        return [
            {
                name: 'Areas',
                kind: 'areas-root',
                expandable: true,
                children: areas.map(area => this.crearNodo(area, undefined, 'area', true))
            },
            {
                name: 'Carreras',
                kind: 'carreras-root',
                expandable: true,
                children: carreras.map(carrera => this.crearNodo(carrera, undefined, 'carrera', false))
            }
        ];
    }

    private async cargarHijos(node: CatalogoNode): Promise<void> {
        if (!node.expandable || node.loading || (node.children && node.children.length > 0)) {
            return;
        }

        const tipologiaId = node.tipologiaId || 0;
        if (!tipologiaId) {
            return;
        }

        node.loading = true;
        try {
            if (node.kind === 'area') {
                const subareas = await this.obtenerTipologiasCache(tipologiaId);
                const childKind = this.selectedCiclo === 'diversificado' ? 'subarea' : 'orientacion';
                node.children = subareas.map(subarea => this.crearNodo(subarea, undefined, childKind, true));
            } else if (node.kind === 'subarea') {
                const ocupacionales = await this.obtenerTipologiasCache(tipologiaId);
                node.children = ocupacionales.map(ocupacional => this.crearNodo(ocupacional));
            } else if (node.kind === 'orientacion' && this.selectedCiclo === 'basico') {
                const ocupacionales = await this.obtenerTipologiasCache(tipologiaId);
                node.children = ocupacionales.map(ocupacional => this.crearNodo(ocupacional));
            }
        } finally {
            node.loading = false;
            this.dataSource.data = this.dataSource.data;
        }
    }

    private async cargarSubareasPorArea(node: CatalogoNode): Promise<void> {
        this.isTableLoading = true;
        const tituloBase = 'Sub Areas';
        this.tableTitle = `${tituloBase} / ${node.name}`;
        this.searchTerm = '';
        this.tableRows = [];

        try {
            const subareas = await this.obtenerTipologiasCache(node.tipologiaId ?? 0);
            this.tableRows = subareas;
        } finally {
            this.isTableLoading = false;
        }
    }

    private async cargarOrientacionesOcupacionales(node: CatalogoNode): Promise<void> {
        this.isTableLoading = true;
        const prefijo = this.selectedCiclo === 'diversificado' ? 'Orientaciones ocupacionales' : 'Orientaciones ocupacionales';
        this.tableTitle = `${prefijo} / ${node.name}`;
        this.searchTerm = '';
        this.tableRows = [];

        try {
            const ocupacionales = await this.obtenerTipologiasCache(node.tipologiaId ?? 0);
            this.tableRows = ocupacionales;
        } finally {
            this.isTableLoading = false;
        }
    }

    private obtenerTipologias(tipologiaId: number): Promise<Tipologia[]> {
        if (!tipologiaId) {
            return Promise.resolve([]);
        }
        return this._registrosService.getTipologias(tipologiaId)
            .then((response) => response?.tipologias || []);
    }

    private obtenerTipologiasCache(tipologiaId: number): Promise<Tipologia[]> {
        if (!tipologiaId) {
            return Promise.resolve([]);
        }
        const cached = this._tipologiasCache.get(tipologiaId);
        if (cached) {
            return Promise.resolve(cached);
        }
        return this.obtenerTipologias(tipologiaId).then((tipologias) => {
            this._tipologiasCache.set(tipologiaId, tipologias);
            return tipologias;
        });
    }

    private crearNodo(
        tipologia: Tipologia,
        children?: CatalogoNode[],
        kind?: CatalogoNode['kind'],
        expandable?: boolean
    ): CatalogoNode {
        return {
            name: tipologia?.descripcion || '(Sin descripcion)',
            tipologiaId: tipologia?.tipologiaId,
            valor1: tipologia?.valor1 || '',
            valor2: tipologia?.valor2 || '',
            children,
            kind,
            expandable: expandable ?? (children ? children.length > 0 : false)
        };
    }

    private mostrarTablaDesdeNodos(node: CatalogoNode, titulo: string): void {
        if (!node?.children || node.children.length === 0) {
            this.tableRows = [];
            this.tableTitle = titulo;
            return;
        }
        this.tableTitle = titulo;
        this.searchTerm = '';
        this.tableRows = node.children.map((child) => ({
            tipologiaId: child.tipologiaId ?? 0,
            descripcion: child.name,
            valor1: child.valor1 || '',
            valor2: child.valor2 || ''
        }));
    }

    get filteredTableRows(): Tipologia[] {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) {
            return this.tableRows;
        }
        return this.tableRows.filter((row) => {
            const descripcion = row.descripcion ?? '';
            const valor1 = row.valor1 ?? '';
            const valor2 = row.valor2 ?? '';
            return (
                descripcion.toLowerCase().includes(term) ||
                valor1.toLowerCase().includes(term) ||
                valor2.toLowerCase().includes(term)
            );
        });
    }

    private resetearTabla(): void {
        this.tableRows = [];
        this.tableTitle = 'Seleccione un elemento';
        this.isTableLoading = false;
        this.selectedNodeId = null;
        this.selectedRootKind = null;
        this.selectedNodeName = '';
        this.searchTerm = '';
    }

    private seleccionarNodoInicial(nodes: CatalogoNode[]): void {
        const root = nodes?.[0];
        if (!root) {
            this.resetearTabla();
            return;
        }
        this.selectedNodeId = null;
        this.selectedRootKind = root.kind ?? null;
        this.selectedNodeName = root.name;
        this.mostrarTablaDesdeNodos(root, root.name);
    }

    private obtenerTipologiaPadreId(): number | null {
        if (this.selectedNodeId) {
            return this.selectedNodeId;
        }
        if (this.selectedRootKind === 'areas-root') {
            return this.selectedCiclo === 'diversificado'
                ? environment.TIPOLOGIA_ID_AREA_CARRERA_DIVERSIFICADO
                : environment.TIPOLOGIA_ID_AREA_CARRERA_BASICO;
        }
        if (this.selectedRootKind === 'orientaciones-root') {
            return this.selectedCiclo === 'diversificado'
                ? environment.TIPOLOGIA_ID_ORIENTACION_DIVERSIFICADO
                : environment.TIPOLOGIA_ID_ORIENTACION_BASICO;
        }
        if (this.selectedRootKind === 'carreras-root') {
            return environment.TIPOLOGIA_ID_CARRERA_DIVERSIFICADO;
        }
        return null;
    }

    eliminarCatalogo(tipologia: Tipologia){
        const message = `Está a punto de eliminar el catálogo ${tipologia.descripcion}. ¿Desea continuar?`;

        this._apiService.open_confirm_dialog(message).subscribe((confirmado) => {
                    if (!confirmado) { return; }

                    this._catalogosService.eliminarTipologia(tipologia.tipologiaId)
                        .then((response:any) => {
                            this._tipologiasCache.clear();
                            this.cargarArbol();
                        })
                });
    }
}
